import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";

const colorMapping = {
  Tickets: "#0055A5",
  Hotels: "#d97706",
  "Train Tickets": "#00918f",
};

export const chartsData = [
  { metric: "Bookings", Tickets: 4200, Hotels: 3200, "Train Tickets": 2500 },
  { metric: "Revenue", Tickets: 12000, Hotels: 9500, "Train Tickets": 7600 },
  { metric: "Cancellations", Tickets: 500, Hotels: 320, "Train Tickets": 210 },
  { metric: "Refunds", Tickets: 8000, Hotels: 6300, "Train Tickets": 5200 },
  { metric: "SLT Revenue", Tickets: 8000, Hotels: 6300, "Train Tickets": 5200 },
  { metric: "Partner Revenue", Tickets: 7000, Hotels: 5800, "Train Tickets": 4600 },
];

const currencyMetrics = new Set(["Revenue", "Refunds", "SLT Revenue", "Partner Revenue"]);
const bookingTypes = Object.keys(colorMapping);

const rowTotal = (row) => row.Tickets + row.Hotels + row["Train Tickets"];

const bookingsRow = chartsData.find((row) => row.metric === "Bookings");
const revenueRow = chartsData.find((row) => row.metric === "Revenue");

const revenueByType = bookingTypes.map((type) => ({
  name: type,
  value: revenueRow[type],
}));

// Illustrative monthly trend leading up to the current period's real total
// (bookingsRow's sum). Historical month-by-month bookings aren't tracked by
// this endpoint yet; once that data exists, swap this for the real series.
const bookingsTrend = [
  { month: "Sep", value: 4800 },
  { month: "Oct", value: 5800 },
  { month: "Nov", value: 6600 },
  { month: "Dec", value: 7500 },
  { month: "Jan", value: 8300 },
  { month: "Feb", value: rowTotal(bookingsRow) },
];

const CustomLabel = (props) => {
  const { x, y, width, value } = props;
  return (
    <text x={x + width / 2} y={y - 6} fill="#64748b" fontSize={11} textAnchor="middle">
      {value.toLocaleString()}
    </text>
  );
};

const ChartLegend = () => (
  <div style={{ display: "flex", gap: 18, marginBottom: 16 }}>
    {Object.entries(colorMapping).map(([label, color]) => (
      <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: "inline-block" }} />
        {label}
      </div>
    ))}
  </div>
);

const ChartCard = ({ title, subtitle, corner, children }) => (
  <div
    style={{
      width: "100%",
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      padding: "20px 22px",
      boxSizing: "border-box",
    }}
  >
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <h3 style={{ margin: "0 0 2px", fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{title}</h3>
      {corner}
    </div>
    {subtitle && <div style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>{subtitle}</div>}
    {children}
  </div>
);

const CustomBarChart = ({ title, data }) => {
  const total = rowTotal(data[0]);
  const prefix = currencyMetrics.has(title) ? "LKR " : "";

  return (
    <ChartCard title={title} subtitle={`Total: ${prefix}${total.toLocaleString()}`}>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 20, right: 10, left: 0, bottom: 0 }} barCategoryGap="30%">
          <CartesianGrid vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="metric" tick={false} axisLine={{ stroke: "#e2e8f0" }} />
          <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
          <Tooltip
            cursor={{ fill: "rgba(0,85,165,0.04)" }}
            contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
            formatter={(value) => value.toLocaleString()}
          />
          {bookingTypes.map((category) => (
            <Bar key={category} dataKey={category} name={category} fill={colorMapping[category]} radius={[4, 4, 2, 2]} barSize={40}>
              <LabelList dataKey={category} content={<CustomLabel />} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const TrendDot = (props) => {
  const { cx, cy, index, value } = props;
  const isLast = index === bookingsTrend.length - 1;
  if (isLast) {
    return (
      <g>
        <text x={cx} y={cy - 14} textAnchor="middle" fontSize={13} fontWeight={700} fill="#0f172a">
          {value.toLocaleString()}
        </text>
        <circle cx={cx} cy={cy} r={5} fill="#0055A5" stroke="#ffffff" strokeWidth={2} />
      </g>
    );
  }
  return <circle cx={cx} cy={cy} r={4} fill="#ffffff" stroke="#0055A5" strokeWidth={2} />;
};

const BookingsTrendLine = () => (
  <ChartCard
    title="Bookings Trend"
    subtitle="Total bookings, all types"
    corner={<span style={{ fontSize: 12, color: "#64748b" }}>Last 6 months</span>}
  >
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={bookingsTrend} margin={{ top: 24, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="bookingsTrendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0055A5" stopOpacity={0.16} />
            <stop offset="100%" stopColor="#0055A5" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
        <YAxis hide domain={["dataMin - 1000", "dataMax + 1500"]} />
        <Tooltip
          contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
          formatter={(value) => value.toLocaleString()}
        />
        <Area type="monotone" dataKey="value" stroke="#0055A5" strokeWidth={2.5} fill="url(#bookingsTrendFill)" dot={<TrendDot />} activeDot={{ r: 6 }} />
      </AreaChart>
    </ResponsiveContainer>
  </ChartCard>
);

const RevenueShareDonut = () => {
  const total = revenueByType.reduce((acc, item) => acc + item.value, 0);
  return (
    <ChartCard title="Revenue Share by Booking Type" subtitle="February 2026">
      <div style={{ position: "relative" }}>
        <ResponsiveContainer width="100%" height={170}>
          <PieChart>
            <Pie
              data={revenueByType}
              dataKey="value"
              nameKey="name"
              innerRadius={55}
              outerRadius={80}
              paddingAngle={2}
              stroke="#ffffff"
              strokeWidth={2}
            >
              {revenueByType.map((entry) => (
                <Cell key={entry.name} fill={colorMapping[entry.name]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
              formatter={(value, name) => [`${value.toLocaleString()} (${((value / total) * 100).toFixed(1)}%)`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <div style={{ fontSize: 11, color: "#64748b" }}>Total</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{total.toLocaleString()}</div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
        {revenueByType.map((entry) => (
          <div key={entry.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12.5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#334155" }}>
              <span style={{ width: 9, height: 9, borderRadius: 3, background: colorMapping[entry.name], display: "inline-block" }} />
              {entry.name}
            </div>
            <span style={{ color: "#64748b" }}>
              {entry.value.toLocaleString()} &middot; {((entry.value / total) * 100).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </ChartCard>
  );
};

export default function MultiChartPage() {
  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", margin: "0 0 2px" }}>Performance by Booking Type</h2>
          <div style={{ fontSize: 12, color: "#64748b" }}>February 2025</div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 1fr)",
          gap: 20,
          marginBottom: 20,
        }}
      >
        <BookingsTrendLine />
        <RevenueShareDonut />
      </div>

      <ChartLegend />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 20,
        }}
      >
        {chartsData.map((chart) => (
          <CustomBarChart key={chart.metric} title={chart.metric} data={[chart]} />
        ))}
      </div>
    </div>
  );
}
